<script setup lang="ts">
import RelatedGames from '../../tournament/RelatedGames.vue';
import MissionReward from '../MissionReward.vue';
import { useMissionCard } from '~/composables/useMissionCard';

const props = defineProps<{
  mission: TMissionOrBadge;
  isFeatured?: boolean;
}>();

const emit = defineEmits<{
  missionOptedIn: [missionId: number];
}>();

const {
  mission,
  isFeatured,
  isModalVisible,
  isRelatedGamesModalVisible,
  isRewardModalVisible,
  formattedHintText,
  rewardData,
  buttonState,
  handleButtonClick,
  handleMissionClick,
} = useMissionCard(props, (missionId) => emit('missionOptedIn', missionId));
</script>

<template>
  <div :class="$style.cardWrapper">
    <div
      :class="[
        $style.missionCard,
        $style.disponiveis,
        isFeatured ? $style.featuredMission : '',
      ]"
      draggable="false"
      @dragstart.prevent
    >
      <!-- Ribbon no topo centralizado -->
      <div
        v-if="mission.ribbon"
        :class="$style.ribbon"
      >
        <span :class="$style.ribbonText">{{ mission.ribbon }}</span>
      </div>

      <div :class="$style.cardContent">
        <!-- Header: Imagem + Info -->
        <div :class="$style.missionBlock">
          <div :class="$style.missionLeft">
            <SmarticoApostatudoSmarticoImage
              :alt="mission.name"
              :class="$style.image"
              :src="mission.image"
            />
          </div>
          <div :class="$style.missionRight">
            <div
              :class="$style.name"
              @click="handleMissionClick('reward')"
            >
              {{ mission.name }}
            </div>
            <div
              :class="$style.description"
              @click="handleMissionClick('reward')"
            >
              {{ mission.description }}
            </div>
          </div>
        </div>

        <!-- Prêmio -->
        <div
          v-if="mission.reward"
          :class="$style.rewardSection"
          @click="handleMissionClick('reward')"
        >
          <NuxtIcon
            :class="$style.rewardIcon"
            :filled="true"
            name="mission/gift"
          />
          <span :class="$style.rewardText">{{ rewardData.reward }}</span>
        </div>

        <!-- Botões -->
        <div :class="$style.buttonsSection">
          <!-- Mobile: Participar primeiro -->
          <button
            v-if="buttonState"
            :class="[$style.participateButton, buttonState.class]"
            :disabled="buttonState.disabled"
            @click.stop="handleButtonClick"
          >
            <NuxtIcon
              :class="$style.buttonIcon"
              :filled="true"
              :name="buttonState.iconName"
            />
            <span :class="[$style.buttonText, buttonState.textClass]">{{
              buttonState.text
            }}</span>
          </button>
          <div
            v-if="mission.hint_text"
            :class="$style.rulesButton"
            @click="handleMissionClick('reward')"
          >
            <NuxtIcon
              :class="$style.rulesIcon"
              name="mission/circle-exclamation"
            />
            <span :class="$style.rulesText">Regras</span>
          </div>
        </div>

        <!-- Jogos Selecionados -->
        <div
          :class="$style.gamesSection"
          @click="handleMissionClick('relatedGames')"
        >
          <div :class="$style.gamesThumbsContainer">
            <div
              v-for="(related, index) in mission.related_games?.slice(0, 5)"
              :key="index"
              :class="$style.gameThumb"
            >
              <NuxtImg
                class="object-cover w-full h-full"
                :src="related?.game_public_meta?.image"
              />
            </div>
          </div>
          <span :class="$style.gamesText">Jogos Elegíveis</span>
        </div>
      </div>

      <!-- Modais -->
      <BaseModalMissionModal v-model="isModalVisible">
        <template #header>
          <NuxtIcon
            class="text-xl text-white"
            name="mission/info"
          />
          <p class="text-lg font-bold text-white">Termos e condições</p>
        </template>
        <div v-if="mission.hint_text">
          <p
            v-for="(line, index) in formattedHintText"
            :key="index"
            class="mb-2 text-white/50 font-normal text-[13px]"
          >
            {{ line }}
          </p>
        </div>
      </BaseModalMissionModal>

      <RelatedGames
        v-model:modal-visible="isRelatedGamesModalVisible"
        :is-loading="false"
        :items="mission.related_games"
        style="display: none"
        title="Jogos Elegíveis"
      />

      <MissionReward
        v-model:modal-visible="isRewardModalVisible"
        :mission="mission"
      />
    </div>
  </div>
</template>

<style module lang="postcss">
/* === CARD WRAPPER === */
.cardWrapper {
  width: 100%;
  min-width: 0;
  padding-top: 10px;
  /* Isola contexto de composição para evitar vazamento de blur/shadow */
  isolation: isolate;
  contain: layout style paint;
}

/* Dentro de swipers: largura fixa */
:global(.afiliado-swiper__items) .cardWrapper {
  width: 165px;
  flex-shrink: 0;
}

@media (min-width: 640px) {
  .cardWrapper {
    padding-top: 14px;
  }

  :global(.afiliado-swiper__items) .cardWrapper {
    width: 260px;
  }
}

/* === CARD BASE === */
.missionCard {
  @apply relative flex flex-col items-start backdrop-blur-[7px] rounded-[9px] overflow-visible select-none;
  width: 100%;
  height: 100%;
  padding: 9px;
  min-width: 0;
}
.missionCard::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(180deg, #1d2329 0%, #1d2329 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
  z-index: -1;
}
@media (min-width: 640px) {
  /* Desktop */
  .missionCard {
    padding: 14px;
    border-radius: 14px;
    backdrop-filter: blur(10px);
  }
}
.disponiveis {
  background: linear-gradient(180deg, #0b1220 0%, #010716 100%);
}
.cardContent {
  @apply flex flex-col justify-between w-full;
  flex: 1;
  gap: 5px;
}
@media (min-width: 640px) {
  .cardContent {
    gap: 7px;
  }
}

/* === RIBBON === */
/* Ribbon no topo centralizado */
.ribbon {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  z-index: 10;
  min-width: 80px;
  width: auto;
  max-width: calc(100% - 46px);
  height: 18px;
  padding: 0 8px;
  background: rgba(29, 35, 41, 0.7);
  backdrop-filter: blur(8px);
  border-radius: 4px;
  border: 1px solid rgba(29, 35, 41, 0.5);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

@media (min-width: 640px) {
  .ribbon {
    height: 22px;
    min-width: 96px;
    padding: 0 10px;
  }
}

.ribbonText {
  font-size: 9px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
}

@media (min-width: 640px) {
  .ribbonText {
    font-size: 11px;
  }
}

/* === HEADER: IMAGEM + INFO === */
.missionBlock {
  @apply flex items-stretch;
  gap: 7px;
}
@media (min-width: 640px) {
  .missionBlock {
    gap: 10px;
  }
}
.missionLeft {
  @apply relative flex-shrink-0;
}
.image {
  @apply rounded-lg overflow-hidden;
  width: 63px;
  height: 63px;
}
@media (min-width: 640px) {
  .image {
    width: 94px;
    height: 94px;
  }
}
.missionRight {
  @apply flex flex-col items-start justify-around flex-1 min-w-0;
  height: 100%;
}
.name {
  @apply w-full overflow-hidden font-bold text-white leading-tight cursor-pointer hover:brightness-75;
  font-size: 12px;
  line-height: 15px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
}
@media (min-width: 640px) {
  .name {
    font-size: 19px;
    line-height: 22px;
  }
}
.description {
  @apply w-full overflow-hidden font-normal leading-tight cursor-pointer hover:brightness-75;
  font-size: 10px;
  line-height: 12px;
  color: #a1a1aa;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
}
@media (min-width: 640px) {
  .description {
    font-size: 15px;
    line-height: 19px;
  }
}

/* === RECOMPENSA === */
.rewardSection {
  @apply flex items-center justify-center w-full cursor-pointer hover:brightness-110 transition-all;
  background: #0d2452;
  backdrop-filter: blur(7px);
  padding: 5px 11px;
  border-radius: 7px;
  height: 27px;
  gap: 7px;
}
@media (min-width: 640px) {
  .rewardSection {
    padding: 8px 17px;
    border-radius: 10px;
    height: 40px;
    gap: 10px;
    backdrop-filter: blur(10px);
  }
}
.rewardIcon {
  font-size: 13px;
  color: white;
}
@media (min-width: 640px) {
  .rewardIcon {
    font-size: 20px;
  }
}
.rewardText {
  @apply font-bold text-center text-white whitespace-nowrap overflow-hidden text-ellipsis;
  font-size: 10px;
  max-width: 100%;
}
@media (min-width: 640px) {
  .rewardText {
    font-size: 14px;
  }
}

/* === BOTÕES === */
.buttonsSection {
  @apply flex items-center justify-center w-full;
  gap: 4px;
}
@media (min-width: 640px) {
  .buttonsSection {
    gap: 6px;
  }
}
.participateButton {
  @apply flex items-center justify-center cursor-pointer transition-all flex-1;
  border: 0.7px solid #fc9984;
  border-radius: 4px;
  padding: 0 7px;
  height: 21px;
  gap: 6px;
}
.participateButton:hover {
  background: rgba(252, 153, 132, 0.1);
}
@media (min-width: 640px) {
  .participateButton {
    border-width: 1px;
    border-radius: 6px;
    padding: 0 10px;
    height: 33px;
    gap: 8px;
  }
}
.buttonIcon {
  font-size: 11px;
  color: #fc9984;
}
@media (min-width: 640px) {
  .buttonIcon {
    font-size: 16px;
  }
}
.buttonText {
  @apply font-bold text-white;
  font-size: 8px;
}
@media (min-width: 640px) {
  .buttonText {
    font-size: 11px;
  }
}
.rulesButton {
  @apply flex items-center justify-center cursor-pointer transition-colors;
  background: rgba(9, 17, 44, 0);
  border: 0.6px solid #30364d;
  border-radius: 5px;
  padding: 0 10px;
  height: 22px;
  gap: 3px;
}
.rulesButton:hover {
  background: rgba(255, 255, 255, 0.05);
}
@media (min-width: 640px) {
  .rulesButton {
    border-width: 1px;
    border-radius: 7px;
    padding: 0 14px;
    height: 33px;
    gap: 5px;
  }
}
.rulesIcon {
  font-size: 11px;
  color: #a1a1aa;
}
@media (min-width: 640px) {
  .rulesIcon {
    font-size: 16px;
  }
}
.rulesText {
  @apply font-normal text-white;
  font-size: 9px;
}
@media (min-width: 640px) {
  .rulesText {
    font-size: 13px;
  }
}

/* === JOGOS SELECIONADOS === */
.gamesSection {
  @apply flex items-center justify-around w-full cursor-pointer transition-colors;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 700px;
  padding: 4px 6px;
  min-height: 27px;
}
.gamesSection:hover {
  background: rgba(255, 255, 255, 0.1);
}
@media (min-width: 640px) {
  .gamesSection {
    border-radius: 1044px;
    padding: 5px 8px;
    min-height: 38px;
  }
}
.gamesThumbsContainer {
  @apply flex items-center;
}
.gameThumb {
  @apply rounded-full overflow-hidden flex-shrink-0;
  width: 19px;
  height: 19px;
  margin-right: -12px;
}
.gameThumb:last-child {
  margin-right: 0;
}
@media (min-width: 640px) {
  .gameThumb {
    width: 28px;
    height: 28px;
    margin-right: -18px;
  }
}
.gamesText {
  @apply font-normal whitespace-nowrap;
  font-size: 8px;
  color: rgba(255, 255, 255, 0.5);
}
@media (min-width: 640px) {
  .gamesText {
    font-size: 13px;
  }
}
</style>
